import { RARITIES, type Rarity } from './enums.js';

/**
 * Single source of truth for how a rarity renders. Every surface (product
 * cards, detail pages, admin badges) reads from here — there are no hardcoded
 * rarity colors in components.
 *
 * The `hex`/`softBg`/`border` values map to CSS custom properties named
 * `--rarity-<name>` etc., so the same scale is available in CSS token files.
 */
export interface RarityMeta {
  readonly rarity: Rarity;
  readonly label: string;
  /** Ordinal tier, 0 (lowest) .. 5 (highest). */
  readonly tier: number;
  /** Foreground / chip text + glow color. */
  readonly hex: string;
  /** Translucent chip background. */
  readonly softBg: string;
  /** Chip border color. */
  readonly border: string;
}

export const RARITY_META: Record<Rarity, RarityMeta> = {
  common: {
    rarity: 'common',
    label: 'Common',
    tier: 0,
    hex: '#9CA3AF',
    softBg: 'rgba(156, 163, 175, 0.12)',
    border: 'rgba(156, 163, 175, 0.35)',
  },
  uncommon: {
    rarity: 'uncommon',
    label: 'Uncommon',
    tier: 1,
    hex: '#22C55E',
    softBg: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.35)',
  },
  rare: {
    rarity: 'rare',
    label: 'Rare',
    tier: 2,
    hex: '#3B82F6',
    softBg: 'rgba(59, 130, 246, 0.12)',
    border: 'rgba(59, 130, 246, 0.35)',
  },
  legendary: {
    rarity: 'legendary',
    label: 'Legendary',
    tier: 3,
    hex: '#A855F7',
    softBg: 'rgba(168, 85, 247, 0.14)',
    border: 'rgba(168, 85, 247, 0.4)',
  },
  mythic: {
    rarity: 'mythic',
    label: 'Mythic',
    tier: 4,
    hex: '#FBBF24',
    softBg: 'rgba(251, 191, 36, 0.14)',
    border: 'rgba(251, 191, 36, 0.4)',
  },
  super: {
    rarity: 'super',
    label: 'Super',
    tier: 5,
    hex: '#EF4444',
    softBg: 'rgba(239, 68, 68, 0.14)',
    border: 'rgba(239, 68, 68, 0.4)',
  },
};

export function rarityMeta(rarity: Rarity): RarityMeta {
  return RARITY_META[rarity];
}

/** Rarities ordered lowest -> highest tier. */
export const RARITY_ORDER: readonly Rarity[] = RARITIES;

export function compareRarity(a: Rarity, b: Rarity): number {
  return RARITY_META[a].tier - RARITY_META[b].tier;
}
